/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useMemo } from "react";
import { Dropdown } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { HiOutlineGlobeAlt } from "react-icons/hi";
import { useUserSession } from "../../context/UserContext";
import {
  normalizeAppLanguage,
  rememberSelectedLanguage,
} from "../../helpers/userPreferences";

interface Language {
  code: string;
  name: string;
}

// Definerer tilgjengelige språk
const availableLanguages: Language[] = [
  {
    code: "en",
    name: "English",
  },
  {
    code: "no",
    name: "Norsk",
  },
];

export const LanguageDropdown: FC = function () {
  const { i18n } = useTranslation();
  const { userSession, updateUserPreferences } = useUserSession();

  // Finn aktivt språk med useMemo for bedre ytelse
  const activeLanguage = useMemo(() => {
    const defaultLang = availableLanguages[0];
    const activeCode =
      normalizeAppLanguage(i18n.resolvedLanguage ?? i18n.language) ?? "en";
    return (
      availableLanguages.find((item) => item.code === activeCode) ?? defaultLang
    );
  }, [i18n.language, i18n.resolvedLanguage]);

  // Håndterer språkendring
  const handleLanguageChange = (lang: string): void => {
    const language = normalizeAppLanguage(lang);
    if (!language) return;
    rememberSelectedLanguage(language, userSession?.user.email);
    void i18n.changeLanguage(language);
    void updateUserPreferences({ language });
  };

  return (
    <Dropdown
      arrowIcon={false}
      inline
      label={
        <div
          className="inline-flex items-center justify-center rounded border border-gray-200 p-1 text-gray-600 hover:bg-gray-50 hover:text-gray-700 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-600 dark:hover:bg-gray-200 dark:hover:text-gray-700 active:scale-95 transition-all duration-150"
          style={{ backgroundColor: "#fafafa" }}
        >
          <span className="sr-only">Velg språk</span>

          {/* Alternativt globus-ikon fra react-icons/hi */}
          <HiOutlineGlobeAlt className="w-6 h-6 text-gray-500" />

          {/* Kode for aktivt språk */}
          <span className="ml-1 text-xs font-medium uppercase">
            {activeLanguage.code}
          </span>
        </div>
      }
      className="z-50"
    >
      {availableLanguages.map((language) => (
        <Dropdown.Item
          key={language.code}
          onClick={() => handleLanguageChange(language.code)}
          className="flex items-center gap-2 px-4 py-2 text-sm"
        >
          <span className="font-medium">{language.name}</span>
        </Dropdown.Item>
      ))}
    </Dropdown>
  );
};
